"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { Box, Button, HStack, Text, useBreakpointValue } from "@chakra-ui/react";
import { FeatureCollection, Geometry } from "geojson";
import { toaster } from "@/components/ui/toaster";
import Map from "./map";
import ReportHazards from "./report-hazards";
import { useHazardDataFetcher } from "../hooks/useHazardDataFetcher";
import SHDrawer, { MOBILE_DRAWER_HEIGHT_RATIO } from "./drawer";
import AlertInfo from "./ui/alert-info";
import NextLink from "@/components/custom-next-link";
import { useMapState } from "./map-state-provider";
const defaultCoords = [-122.4194, 37.7949];
const toggledStatesDefaults = [true, true, true];
interface AddressMapperProps {
  softStoryData: FeatureCollection<Geometry>;
  tsunamiData: FeatureCollection<Geometry>;
  liquefactionData: FeatureCollection<Geometry>;
  femaRiskData: FeatureCollection<Geometry>;
}
export type LayerToggleObjProps = {
  layerIds: string[];
  toggleState: boolean;
};
type ErrorResult = {
  error: true;
  message: string;
};
const isErrorResult = (data: unknown): data is ErrorResult => {
  return typeof data === "object" && data !== null && "error" in data && (data as ErrorResult).error === true;
};
const AddressMapper: React.FC<AddressMapperProps> = t0 => {
  const $ = _c(56);
  const {
    softStoryData,
    tsunamiData,
    liquefactionData,
    femaRiskData
  } = t0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    setSearchComplete
  } = useMapState();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("lon");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialLon = t1;
  let t2;
  if ($[2] !== searchParams) {
    t2 = searchParams.get("lat");
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const initialLat = t2;
  let t3;
  if ($[4] !== searchParams) {
    t3 = searchParams.get("address");
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const initialAddress = t3;
  const validParams = Boolean(initialLon && initialLat && initialAddress);
  let t4;
  if ($[6] !== initialLat || $[7] !== initialLon || $[8] !== validParams) {
    t4 = validParams ? [parseFloat(initialLon as string), parseFloat(initialLat as string)] : defaultCoords;
    $[6] = initialLat;
    $[7] = initialLon;
    $[8] = validParams;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const [lon, lat] = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const [addressHazardData, setAddressHazardData] = useState(t5);
  let t6;
  if ($[11] !== addressHazardData || $[12] !== validParams) {
    t6 = validParams ? addressHazardData : {};
    $[11] = addressHazardData;
    $[12] = validParams;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const displayData = t6;
  const [isHazardDataLoading, setHazardDataLoading] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(true);
  const isMobile = useBreakpointValue({
    base: true,
    md: false
  }) ?? true;
  const bottomPaddingRatio = isMobile && isDrawerOpen ? MOBILE_DRAWER_HEIGHT_RATIO : 0;
  const [toggledStates, setToggledStates] = useState(toggledStatesDefaults);
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      layerIds: [],
      toggleState: true
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const [layerToggleObj, setLayerToggleObj] = useState(t7);
  let t8;
  if ($[15] !== setSearchComplete) {
    t8 = {
      setSearchComplete,
      setHazardDataLoading
    };
    $[15] = setSearchComplete;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const {
    fetchHazardData
  } = useHazardDataFetcher(t8);
  let t9;
  if ($[17] !== searchParams) {
    t9 = searchParams.toString();
    $[17] = searchParams;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const currentQueryString = t9;
  const prepareHref = currentQueryString ? `/prepare?${currentQueryString}` : "/prepare";
  let t10;
  let t11;
  if ($[19] !== fetchHazardData || $[20] !== initialLat || $[21] !== initialLon || $[22] !== validParams) {
    t10 = () => {
      let isCurrent = true;
      const updateHazardData = async coords => {
        ;
        try {
          const values = await fetchHazardData(coords);
          if (isCurrent) {
            setAddressHazardData(values);
          }
        } catch (t12) {
          const error = t12;
          console.error("Error while retrieving data: ", error instanceof Error ? error.message : error?.toString());
          if (isCurrent) {
            setAddressHazardData({
              softStory: null,
              tsunami: null,
              liquefaction: null,
              femaRisk: null
            });
          }
          toaster.create({
            description: "Could not retrieve hazard data",
            type: "error",
            duration: 5000,
            closable: true
          });
        }
      };
      if (!validParams) {
        return;
      }
      updateHazardData([parseFloat(initialLon as string), parseFloat(initialLat as string)]);
      return () => {
        isCurrent = false;
      };
    };
    t11 = [validParams, initialLon, initialLat, fetchHazardData];
    $[19] = fetchHazardData;
    $[20] = initialLat;
    $[21] = initialLon;
    $[22] = validParams;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[25] !== femaRiskData || $[26] !== liquefactionData || $[27] !== softStoryData || $[28] !== tsunamiData) {
    t12 = () => {
      const sources = [{
        name: "Soft Story Buildings",
        data: softStoryData
      }, {
        name: "Tsunami Zones",
        data: tsunamiData
      }, {
        name: "Liquefaction Zones",
        data: liquefactionData
      }, {
        name: "Fema Risk Zones",
        data: femaRiskData
      }];
      const errors = sources.filter(_temp).map(_temp2);
      if (errors.length > 0 && !toaster.isVisible("data-load-failed")) {
        toaster.create({
          id: "data-load-failed",
          title: "Data Load Error",
          description: errors.join(" | "),
          type: "error",
          duration: 5000,
          closable: true
        });
      }
    };
    t13 = [softStoryData, tsunamiData, liquefactionData, femaRiskData];
    $[25] = femaRiskData;
    $[26] = liquefactionData;
    $[27] = softStoryData;
    $[28] = tsunamiData;
    $[29] = t12;
    $[30] = t13;
  } else {
    t12 = $[29];
    t13 = $[30];
  }
  useEffect(t12, t13);
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Box hideBelow="sm"><AlertInfo message="72% chance of major Bay Area earthquake in the next 30 years" /></Box>;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== displayData || $[33] !== isHazardDataLoading || $[34] !== toggledStates) {
    t15 = <ReportHazards addressHazardData={displayData} isHazardDataLoading={isHazardDataLoading} toggledStates={toggledStates} setToggledStates={setToggledStates} setLayerToggleObj={setLayerToggleObj} isInDrawer={true} />;
    $[32] = displayData;
    $[33] = isHazardDataLoading;
    $[34] = toggledStates;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== pathname || $[37] !== prepareHref) {
    t16 = pathname !== "/prepare" && <HStack bg="gray.50" p="5" mt="4"><Text textStyle="textStart" layerStyle="text">{"Take action & Prepare for earthquake"}</Text><Button asChild={true} size="sm"><NextLink href={prepareHref}>Start</NextLink></Button></HStack>;
    $[36] = pathname;
    $[37] = prepareHref;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== isDrawerOpen || $[40] !== t15 || $[41] !== t16) {
    t17 = <SHDrawer open={isDrawerOpen} onOpenChange={setDrawerOpen} title="Risk Layers" footerText={t14}>{t15}{t16}</SHDrawer>;
    $[39] = isDrawerOpen;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== bottomPaddingRatio || $[44] !== femaRiskData || $[45] !== initialAddress || $[46] !== lat || $[47] !== layerToggleObj || $[48] !== liquefactionData || $[49] !== lon || $[50] !== softStoryData || $[51] !== tsunamiData) {
    t18 = <Map lon={lon} lat={lat} address={initialAddress} softStoryData={softStoryData} tsunamiData={tsunamiData} liquefactionData={liquefactionData} femaRiskData={femaRiskData} layerToggleObj={layerToggleObj} bottomPaddingRatio={bottomPaddingRatio} />;
    $[43] = bottomPaddingRatio;
    $[44] = femaRiskData;
    $[45] = initialAddress;
    $[46] = lat;
    $[47] = layerToggleObj;
    $[48] = liquefactionData;
    $[49] = lon;
    $[50] = softStoryData;
    $[51] = tsunamiData;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t17 || $[54] !== t18) {
    t19 = <Box w="full" h="full" m="auto" position="relative"><Box h="full" overflow="hidden">{t17}{t18}</Box></Box>;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  return t19;
};
export default AddressMapper;
function _temp(source) {
  return isErrorResult(source.data);
}
function _temp2(source_0) {
  return `${source_0.name}: ${(source_0.data as unknown as ErrorResult).message || "Unknown error"}`;
}